import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Button } from './Button';
import { Modal } from './Modal';

interface QRCodeDisplayProps {
  employeeId?: string;
  employeeKey?: string;
  employeeName?: string;
  baseUrl?: string;
  size?: number;
  className?: string;
}

export const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  employeeId,
  employeeKey,
  employeeName,
  baseUrl = window.location.origin,
  size = 200,
  className = '',
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareInProgress, setShareInProgress] = useState(false);
  const [pdfInProgress, setPdfInProgress] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const generateQRUrl = (idOrKey: string) => {
    return `${baseUrl}/employees/${encodeURIComponent(idOrKey)}`;
  };

  const generateQRCode = async (idOrKey: string) => {
    if (!idOrKey) return;
    setIsLoading(true);
    setError(null);
    try {
      const url = generateQRUrl(idOrKey);
      const dataUrl = await QRCode.toDataURL(url, {
        width: size,
        margin: 2,
        color: { dark: '#000000', light: '#FFFFFF' },
        errorCorrectionLevel: 'M',
      });
      setQrCodeDataUrl(dataUrl);
    } catch (err) {
      setError('Failed to generate QR code');
    } finally {
      setIsLoading(false);
    }
  }; 
  useEffect(() => {
    const token = employeeKey || employeeId || '';
    generateQRCode(token);
  }, [employeeKey, employeeId, size]);

  const getQrUrl = () => {
    const token = employeeKey || employeeId || '';
    return generateQRUrl(token);
  };

  const loadJsPDF = async (): Promise<any> => {
    const globalAny = window as any;
    if (globalAny.jspdf?.jsPDF) return globalAny.jspdf.jsPDF;
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load jsPDF'));
      document.body.appendChild(script);
    });
    return (window as any).jspdf.jsPDF;
  };

  const handleDownloadPdf = async () => {
    if (!qrCodeDataUrl) return;
    try {
      setPdfInProgress(true);
      const jsPDF = await loadJsPDF();
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const margin = 20;
      const qrSizeMm = 60;
      const pageWidth = doc.internal.pageSize.getWidth();
      const x = (pageWidth - qrSizeMm) / 2;
      doc.setFontSize(14);
      const title = employeeName ? `QR Code - ${employeeName}` : 'QR Code';
      doc.text(title, pageWidth / 2, margin, { align: 'center' as any });
      doc.addImage(qrCodeDataUrl, 'PNG', x, margin + 10, qrSizeMm, qrSizeMm);
      const url = getQrUrl();
      doc.setFontSize(10);
      doc.text(url, pageWidth / 2, margin + 10 + qrSizeMm + 8, { align: 'center' as any, maxWidth: pageWidth - margin * 2 });
      const fileName = (employeeName ? employeeName.replace(/\s+/g, '_') : 'qr-code') + '.pdf';
      doc.save(fileName);
    } catch (e) {
    } finally {
      setPdfInProgress(false);
    }
  };

  const handleShare = async () => {
    try {
      setShareInProgress(true);
      const url = getQrUrl();
      if ((navigator as any).share) {
        const res = await fetch(qrCodeDataUrl);
        const blob = await res.blob();
        const files = [new File([blob], 'qr-code.png', { type: 'image/png' })];
        const shareData: any = { title: employeeName || 'QR Code', text: 'Scan to view employee', url };
        if ((navigator as any).canShare?.({ files })) {
          shareData.files = files;
        }
        await (navigator as any).share(shareData);
      } else {
        setShareOpen(true);
      }
    } catch (e) {
    } finally {
      setShareInProgress(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Generating QR Code...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-center p-4 ${className}`}>
        <div className="text-red-600 mb-2">{error}</div>
        <Button variant="outline" onClick={() => generateQRCode(employeeKey || employeeId || '')}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={`qr-code-display ${className}`}>
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="relative p-4 bg-white rounded-xl shadow-md border">
            <div className="absolute inset-0 -z-10 rounded-xl" style={{
              background: 'radial-gradient(120px 120px at 10% 10%, rgba(59,130,246,0.08), transparent), radial-gradient(140px 140px at 90% 20%, rgba(99,102,241,0.08), transparent)'
            }} />
            <img
              src={qrCodeDataUrl}
              alt={`QR Code for ${employeeName || 'Employee'}`}
              className="block"
              style={{ width: size, height: size }}
            />
          </div>
        </div>  
        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={handleDownloadPdf} disabled={pdfInProgress}>
            {pdfInProgress ? 'Preparing PDF...' : 'Download PDF'}
          </Button>
          <Button variant="primary" onClick={handleShare} disabled={shareInProgress}>
            {shareInProgress ? 'Sharing…' : 'Share'}
          </Button>
        </div>
      </div>
      <Modal
        open={shareOpen}
        title="Share QR Code" 
        onConfirm={() => setShareOpen(false)}
        confirmText="Close" 
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={async () => {
                await navigator.clipboard?.writeText(getQrUrl());
                setShareOpen(false);
              }}
            >
              Copy Link
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const subject = encodeURIComponent(employeeName ? `QR for ${employeeName}` : 'QR Code');
                const body = encodeURIComponent(`Link: ${getQrUrl()}`);
                window.open(`mailto:?subject=${subject}&body=${body}`);
                setShareOpen(false);
              }}
            >
              Email
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const text = encodeURIComponent(getQrUrl());
                window.open(`https://wa.me/?text=${text}`, '_blank');
                setShareOpen(false);
              }}
            >
              WhatsApp
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const text = encodeURIComponent(getQrUrl());
                window.open(`https://t.me/share/url?url=${text}`, '_blank');
                setShareOpen(false);
              }}
            >
              Telegram
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const url = encodeURIComponent(getQrUrl());
                const text = encodeURIComponent(employeeName ? `QR for ${employeeName}` : 'QR Code');
                window.open(`https://x.com/intent/tweet?url=${url}&text=${text}`, '_blank');
                setShareOpen(false);
              }}
            >
              X
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const url = encodeURIComponent(getQrUrl());
                const title = encodeURIComponent(employeeName || 'QR Code');
                window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}`, '_blank');
                setShareOpen(false);
              }}
            >
              LinkedIn
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                const a = document.createElement('a');
                a.href = qrCodeDataUrl;
                a.download = (employeeName ? employeeName.replace(/\s+/g, '_') : 'qr-code') + '.png';
                document.body.appendChild(a);
                a.click();
                a.remove();
                setShareOpen(false);
              }}
            >
              Download PNG
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default QRCodeDisplay;


